import React, { useCallback, useEffect, useRef, useState } from "react";
import { Mic, MicOff, Play, Pause, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/contexts/LangContext";
import api from "@/lib/api";

/**
 * VoiceRecorder
 *
 * A 60-second voice-note recorder using the MediaRecorder API. On stop,
 * uploads the blob to `/api/guestbook/voice` and calls `onUploaded({ url,
 * duration_ms })` so the parent can attach it to a guestbook entry.
 *
 * Progressive: hides itself entirely on browsers without MediaRecorder or
 * getUserMedia. Falls back gracefully on iOS Safari which supports webm
 * only in newer versions — we ask for audio/mp4 first.
 */

const COPY = {
  en: {
    kicker: "Voice note",
    hint: "Up to 60 seconds. Optional — you can leave text only.",
    record: "Record",
    stop: "Stop",
    max: "60s max",
    uploading: "Sending…",
    ready: "Attached",
    remove: "Remove",
    denied: "Microphone blocked — check your browser permissions.",
    unsupported: "Your browser can't record — text still works, koumbare.",
    error: "Recording failed. Try again.",
    uploadFail: "Voice upload failed. Try again.",
  },
  fr: {
    kicker: "Message vocal",
    hint: "60 secondes max. Optionnel — le texte suffit.",
    record: "Enregistrer",
    stop: "Arrêter",
    max: "60s max",
    uploading: "Envoi…",
    ready: "Ajouté",
    remove: "Retirer",
    denied: "Micro bloqué — vérifie les autorisations.",
    unsupported: "Ton navigateur ne sait pas enregistrer — le texte marche, koumbare.",
    error: "L'enregistrement a échoué. Réessaie.",
    uploadFail: "L'envoi a échoué. Réessaie.",
  },
  el: {
    kicker: "Φωνητικό μήνυμα",
    hint: "Έως 60 δευτερόλεπτα. Προαιρετικό — μόνο κείμενο επιτρέπεται.",
    record: "Ηχογράφηση",
    stop: "Στοπ",
    max: "60δ max",
    uploading: "Αποστολή…",
    ready: "Επισυνάφθηκε",
    remove: "Αφαίρεση",
    denied: "Μικρόφωνο μπλοκαρισμένο — δες τις άδειες.",
    unsupported: "Ο browser δεν υποστηρίζει ηχογράφηση — γράψε κείμενο, κουμπάρε.",
    error: "Η ηχογράφηση απέτυχε. Ξαναπροσπάθησε.",
    uploadFail: "Η αποστολή απέτυχε. Ξαναπροσπάθησε.",
  },
  pl: {
    kicker: "Wiadomość głosowa",
    hint: "Do 60 sekund. Opcjonalnie — możesz zostawić tylko tekst.",
    record: "Nagraj",
    stop: "Zatrzymaj",
    max: "60s max",
    uploading: "Wysyłanie…",
    ready: "Dodano",
    remove: "Usuń",
    denied: "Mikrofon zablokowany — sprawdź uprawnienia.",
    unsupported: "Twoja przeglądarka nie nagrywa — tekst zawsze działa, kumie.",
    error: "Nagrywanie nie powiodło się. Spróbuj ponownie.",
    uploadFail: "Nie udało się wysłać. Spróbuj ponownie.",
  },
};

const MAX_SECONDS = 60;

const supported = () =>
  typeof window !== "undefined" &&
  typeof MediaRecorder !== "undefined" &&
  navigator.mediaDevices &&
  typeof navigator.mediaDevices.getUserMedia === "function";

const pickMime = () => {
  const candidates = [
    "audio/webm;codecs=opus",
    "audio/webm",
    "audio/mp4",
    "audio/ogg;codecs=opus",
    "audio/ogg",
  ];
  for (const m of candidates) {
    try { if (MediaRecorder.isTypeSupported(m)) return m; } catch { /* ignore */ }
  }
  return "";
};

const fmt = (ms) => {
  const s = Math.floor(ms / 1000);
  return `${String(Math.floor(s / 60)).padStart(1, "0")}:${String(s % 60).padStart(2, "0")}`;
};

const VoiceRecorder = ({ onUploaded, disabled }) => {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  const [recording, setRecording] = useState(false);
  const [elapsedMs, setElapsedMs] = useState(0);
  const [blobUrl, setBlobUrl] = useState(null);
  const [uploaded, setUploaded] = useState(null); // { url, duration_ms }
  const [uploading, setUploading] = useState(false);
  const [playing, setPlaying] = useState(false);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const startedAtRef = useRef(0);
  const audioRef = useRef(null);

  useEffect(() => () => {
    // Cleanup on unmount
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    if (timerRef.current) clearInterval(timerRef.current);
    if (blobUrl) URL.revokeObjectURL(blobUrl);
  }, [blobUrl]);

  const stop = useCallback(() => {
    try { mediaRef.current?.stop(); } catch { /* ignore */ }
    try { streamRef.current?.getTracks().forEach((t) => t.stop()); } catch { /* ignore */ }
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    setRecording(false);
  }, []);

  const record = useCallback(async () => {
    if (!supported()) { toast.error(c.unsupported); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const mimeType = pickMime();
      const mr = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (ev) => { if (ev.data && ev.data.size > 0) chunksRef.current.push(ev.data); };
      mr.onstop = async () => {
        const type = mediaRef.current?.mimeType || "audio/webm";
        const blob = new Blob(chunksRef.current, { type });
        const url = URL.createObjectURL(blob);
        setBlobUrl(url);
        // Upload immediately
        setUploading(true);
        try {
          const ext = type.includes("mp4") ? "m4a" : type.includes("ogg") ? "ogg" : "webm";
          const form = new FormData();
          form.append("file", blob, `voice.${ext}`);
          const { data } = await api.post("/guestbook/voice", form, {
            headers: { "Content-Type": "multipart/form-data" },
          });
          const dur = elapsedMs;
          setUploaded({ url: data.url, duration_ms: dur });
          if (typeof onUploaded === "function") onUploaded({ url: data.url, duration_ms: dur });
        } catch {
          toast.error(c.uploadFail);
        } finally {
          setUploading(false);
        }
      };
      mr.start(200);
      setElapsedMs(0);
      setBlobUrl(null);
      setUploaded(null);
      startedAtRef.current = Date.now();
      setRecording(true);
      timerRef.current = setInterval(() => {
        const ms = Date.now() - startedAtRef.current;
        setElapsedMs(ms);
        if (ms >= MAX_SECONDS * 1000) stop();
      }, 200);
    } catch (err) {
      if (err && (err.name === "NotAllowedError" || err.name === "SecurityError")) {
        toast.error(c.denied);
      } else {
        toast.error(c.error);
      }
    }
  }, [c, elapsedMs, onUploaded, stop]);

  const togglePlay = () => {
    const a = audioRef.current;
    if (!a) return;
    if (a.paused) { a.play(); setPlaying(true); }
    else { a.pause(); setPlaying(false); }
  };

  const remove = () => {
    if (blobUrl) URL.revokeObjectURL(blobUrl);
    setBlobUrl(null);
    setUploaded(null);
    setElapsedMs(0);
    if (typeof onUploaded === "function") onUploaded(null);
  };

  if (!supported()) return null;

  const pct = Math.min(100, (elapsedMs / (MAX_SECONDS * 1000)) * 100);

  return (
    <div className="rounded-lg border border-border bg-foreground/[0.02] p-4 space-y-3" data-testid="voice-recorder">
      <div className="flex items-center justify-between">
        <p className="editorial-kicker text-foreground/70 inline-flex items-center gap-2">
          <Mic className="w-3.5 h-3.5 text-accent" />
          {c.kicker}
        </p>
        <span className="text-[10px] text-foreground/50 tabular-nums">{fmt(elapsedMs)} / {fmt(MAX_SECONDS * 1000)}</span>
      </div>
      <p className="text-[11px] text-foreground/55">{c.hint}</p>

      {/* Progress ring while recording */}
      {recording && (
        <div className="h-1 w-full bg-foreground/10 rounded-full overflow-hidden">
          <div className="h-full bg-accent transition-[width] duration-150" style={{ width: `${pct}%` }} />
        </div>
      )}

      {/* Playback if we have a recorded blob */}
      {(blobUrl || uploaded) && (
        <div className="flex items-center gap-3 rounded-md bg-background/70 px-3 py-2 border border-border/70">
          <button
            type="button"
            onClick={togglePlay}
            disabled={!blobUrl}
            aria-label={playing ? "Pause" : "Play"}
            className="w-8 h-8 rounded-full bg-foreground text-background inline-flex items-center justify-center hover:bg-foreground/90 disabled:opacity-50"
            data-testid="voice-play"
          >
            {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          </button>
          <div className="flex-1 min-w-0">
            <p className="text-[12px] text-foreground/75 truncate">
              {uploading ? c.uploading : uploaded ? c.ready : fmt(elapsedMs)}
            </p>
          </div>
          <button
            type="button"
            onClick={remove}
            aria-label={c.remove}
            className="w-8 h-8 rounded-md text-destructive/80 hover:text-destructive hover:bg-destructive/10 inline-flex items-center justify-center"
            data-testid="voice-remove"
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
          {blobUrl && (
            <audio
              ref={audioRef}
              src={blobUrl}
              onEnded={() => setPlaying(false)}
              onPause={() => setPlaying(false)}
              className="hidden"
            />
          )}
        </div>
      )}

      {/* Record / Stop */}
      {!blobUrl && !uploaded && (
        <button
          type="button"
          onClick={recording ? stop : record}
          disabled={disabled || uploading}
          data-testid="voice-record-btn"
          className={
            "w-full inline-flex items-center justify-center gap-2 h-10 rounded-lg text-[11px] tracking-[0.16em] uppercase transition-colors " +
            (recording
              ? "bg-destructive text-background hover:bg-destructive/90"
              : "border border-accent/40 text-accent hover:border-accent hover:bg-accent/10")
          }
        >
          {uploading ? (
            <><Loader2 className="w-3.5 h-3.5 animate-spin" /> {c.uploading}</>
          ) : recording ? (
            <><MicOff className="w-3.5 h-3.5" /> {c.stop}</>
          ) : (
            <><Mic className="w-3.5 h-3.5" /> {c.record}</>
          )}
        </button>
      )}
    </div>
  );
};

export default VoiceRecorder;
